// src/components/BlogPostTemplate.tsx
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ChevronLeft, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SectionHeading from '@/components/SectionHeading';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import BlogPostCard from '@/components/BlogPostCard';
import { blogPosts } from '@/data/blogPosts';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  date: string;
  readTime: string;
  category: string;
  tags?: string[];
}

const BlogPostTemplate = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Simulating API fetch
    setTimeout(() => {
      const postId = Number(id);
      const currentPost = blogPosts.find(post => post.id === postId);
      
      if (currentPost) {
        setPost(currentPost);
        
        // Get 4 related posts (excluding current post)
        const related = blogPosts
          .filter(p => p.id !== postId)
          .sort(() => 0.5 - Math.random()) // Shuffle array
          .slice(0, 4);
        
        setRelatedPosts(related);
      }
      
      setLoading(false);
    }, 500);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container-custom py-24 text-center">
          <div className="animate-pulse h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
          <div className="animate-pulse h-4 bg-gray-200 rounded w-1/2 mx-auto mb-12"></div>
          <div className="animate-pulse h-64 bg-gray-200 rounded w-full mb-8"></div>
          <div className="animate-pulse h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="animate-pulse h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="animate-pulse h-4 bg-gray-200 rounded w-3/4 mb-8"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container-custom py-24 text-center">
          <h1 className="text-3xl font-bold">Članek ni najden</h1>
          <p className="mt-4">Oprostite, iskani članek ne obstaja.</p>
          <Button className="mt-8 bg-secondary" asChild>
            <Link to="/nasveti-blog">Nazaj na vse članke</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <ScrollToTop />
      
      <main className="py-24">
        <div className="container-custom max-w-4xl">
          <div className="mb-8">
            <Button variant="outline" className="mb-6" asChild>
              <Link to="/nasveti-blog" className="flex items-center">
                <ChevronLeft size={16} className="mr-2" /> Nazaj na vse članke
              </Link>
            </Button>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary/10 text-secondary rounded-full">
                  {post.category}
                </span>
                
                {post.tags?.map((tag) => (
                  <span key={tag} className="inline-flex items-center px-3 py-1 text-xs font-medium bg-stone-100 text-stone-700 rounded-full">
                    <Tag size={12} className="mr-1" /> {tag}
                  </span>
                ))}
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{post.title}</h1>
              
              <div className="flex text-sm text-stone-500 mb-8">
                <span className="flex items-center mr-4">
                  <Calendar size={16} className="mr-1" /> {post.date}
                </span>
                <span className="flex items-center">
                  <Clock size={16} className="mr-1" /> {post.readTime} branja
                </span>
              </div>
              
              <div className="rounded-lg overflow-hidden mb-8 h-[400px]">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div 
                className="prose prose-stone prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </motion.div>
          </div>
          
          {relatedPosts.length > 0 && (
            <section className="mt-16 pt-16 border-t border-stone-200">
              <SectionHeading 
                title="Podobni članki"
                subtitle="Preberite tudi druge zanimive vsebine"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                {relatedPosts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostTemplate;